import React, { useState } from 'react';
import {
  CONTACT_INFO,
  GOOGLE_MAPS_DIRECTIONS_URL,
  GOOGLE_MAPS_EMBED_URL,
} from '../constants';
import SectionHeading from './SectionHeading';

const initialFormData = {
  name: '',
  email: '',
  telefon: '',
  nachricht: '',
};

const Kontakt = ({ onBackHome }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = ({ target }) => {
    setIsSubmitted(false);
    setFormData((current) => ({
      ...current,
      [target.name]: target.value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsSubmitted(true);
    setFormData(initialFormData);
  };

  return (
    <div className="section-surface section-surface-tinted">
      <div className="mx-auto max-w-7xl px-6 pb-20 pt-4 lg:px-10 lg:pb-24 lg:pt-6">
        <button
          type="button"
          onClick={onBackHome}
          className="mb-6 inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-5 py-2.5 text-sm font-semibold text-stone-900 shadow-sm transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
        >
          ← Zurück
        </button>

        <div className="grid items-start gap-10 lg:grid-cols-[0.95fr_1.05fr]">
          <div>
            <SectionHeading
              title="So erreichst du uns"
              description="Anrufen, schreiben oder direkt die Route in Google Maps öffnen."
            />

            <div className="mt-10 space-y-4">
              <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
                <p className="text-sm uppercase tracking-[0.28em] text-stone-600">
                  Adresse
                </p>
                <p className="mt-3 text-lg font-medium text-stone-950">
                  {CONTACT_INFO.addressLines[0]}
                </p>
                <p className="mt-1 text-base text-stone-700">
                  {CONTACT_INFO.addressLines[1]}
                </p>
                <div className="relative mt-4 overflow-hidden rounded-2xl border border-stone-200">
                  <iframe
                    title="Google Maps"
                    src={GOOGLE_MAPS_EMBED_URL}
                    className="h-44 w-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <a
                    href={GOOGLE_MAPS_DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Route in Google Maps öffnen"
                    className="absolute inset-0"
                  />
                  <div className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-stone-800 shadow-sm ring-1 ring-stone-200">
                    Klicken für Route
                  </div>
                </div>
                <a
                  href={CONTACT_INFO.emailHref}
                  className="mt-4 inline-block text-sm font-semibold text-orange-700"
                >
                  {CONTACT_INFO.email}
                </a>
              </div>

              <div className="rounded-[2rem] border border-orange-100 bg-orange-50 p-6 text-stone-950 shadow-sm">
                <p className="text-sm uppercase tracking-[0.28em] text-orange-700">
                  Öffnungszeiten
                </p>
                <div className="mt-5 space-y-4">
                  {CONTACT_INFO.openingHours.map((entry) => (
                    <div
                      key={entry.days}
                      className="flex items-center justify-between gap-4 border-b border-orange-100 pb-3 last:border-b-0 last:pb-0"
                    >
                      <span className="text-sm text-stone-700">
                        {entry.days}
                      </span>
                      <span className="text-sm font-semibold text-stone-950">
                        {entry.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[2.25rem] border border-stone-200 bg-white p-7 shadow-[0_30px_80px_rgba(28,25,23,0.12)] md:p-8">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm uppercase tracking-[0.28em] text-orange-700">
                  Nachricht senden
                </p>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-stone-950">
                  Bestellungen oder Rückruf anfragen
                </h3>
              </div>
            </div>

            <div className="mt-8 rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
              <p className="text-sm uppercase tracking-[0.28em] text-stone-600">
                Telefon
              </p>
              <a
                href={CONTACT_INFO.phoneHref}
                className="mt-3 block text-xl font-semibold tracking-tight text-stone-950 hover:text-orange-700"
              >
                {CONTACT_INFO.phone}
              </a>
              <p className="mt-2 text-sm leading-6 text-stone-700">
                Für Bestellungen und Rückfragen.
              </p>
            </div>

            <div className="mt-6 rounded-[2rem] border border-[#1DA851] bg-[#25D366] p-6 shadow-sm">
              <p className="text-sm uppercase tracking-[0.28em] text-stone-950/80">
                WhatsApp
              </p>
              <a
                href={CONTACT_INFO.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 block text-xl font-semibold tracking-tight text-stone-950 hover:text-stone-950/80"
              >
                {CONTACT_INFO.whatsapp || CONTACT_INFO.phone}
              </a>
              <p className="mt-2 text-sm leading-6 text-stone-950/80">
                Chat öffnen und Nachricht senden.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid gap-5 md:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-stone-700">
                    Name
                  </span>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Ihr Name"
                  />
                </label>

                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-stone-700">
                    E-Mail
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="name@beispiel.de"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-stone-700">
                  Telefon
                </span>
                <input
                  type="tel"
                  name="telefon"
                  value={formData.telefon}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="+49 ..."
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-stone-700">
                  Nachricht
                </span>
                <textarea
                  name="nachricht"
                  value={formData.nachricht}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="form-input resize-none"
                  placeholder="Datum, Uhrzeit, Personenanzahl oder Lieferwunsch"
                />
              </label>

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center rounded-full bg-orange-700 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-orange-800"
              >
                Anfrage absenden
              </button>

              <p className="text-sm text-stone-600">
                Das Formular bleibt lokal im Frontend. Für den nächsten Schritt
                könnte man es an E-Mail, WhatsApp oder ein Backend anbinden.
              </p>

              {isSubmitted ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  Vielen Dank. Ihre Anfrage wurde vorbereitet und kann jetzt an
                  einen echten Service angebunden werden.
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Kontakt;
